import type { Vec3 } from "./entityData";

/**
 * `initialPlacement.ts` — where a model lands the first time it is added to a
 * scene.
 *
 * Unity places a new model by its BOUNDING BOX against the scene anchor, and it
 * has two rules depending on how the model arrived (traced on `origin/main`,
 * 2026-10-05):
 *
 * - **`"center-on-anchor"` — import from a file.** The importer's
 *   `BaseImporterModel.WrapAndShiftOrigin` puts the box's CENTRE on the anchor
 *   (`anchor - boundsOffset`), and `AssetFactory.CreateAsync(path, …)` scales
 *   about that centre, so the centre stays there.
 * - **`"rest-on-anchor"` — add from the asset library.**
 *   `ProjectModelSlim.WrapAndShiftOrigin` centres the geometry on the new root
 *   and lifts the root by `extents.y`, so the box's BOTTOM rests on the anchor
 *   plane.
 *
 * Both centre the box on the anchor in x/z. The persisted root snapshot is
 * whatever pose that produced, and both clients apply a root snapshot
 * unconditionally on load, so the placement is decided once, here, and then
 * travels as data.
 *
 * **Web deviation (user direction, 2026-10-05):** neither Unity rule keeps a
 * model off the table — a file-imported model taller than twice the anchor
 * height (~0.69 m) starts with its lower half through the table top. Here the
 * result is lifted so the box never starts below {@link groundY}. Smaller
 * models land exactly where Unity puts them.
 *
 * The web used to place neither way: a new root was written as identity, so a
 * model sat wherever its file's own origin put it — the reported case was a
 * screwdriver modelled 0.23 m below its origin, which landed under the table.
 */
export type InitialPlacementRule = "center-on-anchor" | "rest-on-anchor";

/**
 * Unity `PlacementModelBase.m_anchorPoint` (`PlacementModelBase.cs:31`, the
 * same value in `DesktopEditor.prefab`), in the scene's own frame: the faint
 * dot 0.343 m above the table centre. Its x and z are zero, so it is the same
 * point in Unity's snapshot space and in three's world space.
 */
export const SCENE_ANCHOR_POINT: Vec3 = Object.freeze([0, 0.343, 0]) as unknown as Vec3;

/** The table top — Unity's scene space and the web's ground plane are both at y = 0. */
export const SCENE_TABLE_TOP_Y = 0;

/**
 * The world-space translation that places a model by {@link rule}, given its
 * current axis-aligned bounds. All three vectors are in the same frame (three
 * world metres); the caller adds the result to the root's world position.
 *
 * Returns null for bounds that are empty or not finite (a model with no
 * geometry has nothing to place by).
 */
export function initialPlacementOffset(
  boundsMin: Vec3,
  boundsMax: Vec3,
  rule: InitialPlacementRule,
  anchor: Vec3 = SCENE_ANCHOR_POINT,
  groundY: number = SCENE_TABLE_TOP_Y,
): Vec3 | null {
  const finite = [...boundsMin, ...boundsMax].every((v) => Number.isFinite(v));
  if (!finite || boundsMax[0] < boundsMin[0] || boundsMax[1] < boundsMin[1] || boundsMax[2] < boundsMin[2]) {
    return null;
  }
  const centerX = (boundsMin[0] + boundsMax[0]) / 2;
  const centerY = (boundsMin[1] + boundsMax[1]) / 2;
  const centerZ = (boundsMin[2] + boundsMax[2]) / 2;
  let dy = rule === "center-on-anchor" ? anchor[1] - centerY : anchor[1] - boundsMin[1];
  // Never start below the table (the web deviation above).
  if (boundsMin[1] + dy < groundY) dy = groundY - boundsMin[1];
  return [anchor[0] - centerX, dy, anchor[2] - centerZ];
}
